const API_URL = import.meta.env.VITE_API_URL || '';

// Types
export interface User {
  id: string;
  pin?: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role: string;
  is_admin: boolean;
  assigned_store_id: string | null;
  territory_ids: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface Store {
  id: string;
  store_number: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  district: string;
  market: string;
  region: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface QuestionGroup {
  id: string;
  name: string;
  code: string;
  description: string;
  display_order: number;
  is_active: boolean;
  questions?: Question[];
}

export interface Question {
  id: string;
  group_id: string;
  text: string;
  description: string;
  display_order: number;
  is_required: boolean;
  requires_photo_on_fail: boolean;
  default_severity: string;
  is_active: boolean;
}

export interface Case {
  id: string;
  visit_id: string;
  store_id: string;
  question_id: string;
  answer_id: string;
  assigned_to: string;
  severity: string;
  status: string;
  due_date: string;
  notes: string;
  resolution_notes: string;
  resolved_at: string;
  resolved_by: string;
  created_at: string;
  store_name?: string;
  question_text?: string;
  assigned_user_name?: string;
}

export interface Visit {
  id: string;
  store_id: string;
  user_id: string;
  started_at: string;
  completed_at: string;
  score: number;
  status: string;
  store_name?: string;
  user_name?: string;
}

export interface CompanyReport {
  total_stores: number;
  total_visits_this_month: number;
  average_score: number;
  open_cases: {
    critical: number;
    moderate: number;
    minor: number;
    total: number;
    overdue: number;
  };
  districts: string[];
  markets: string[];
  score_trend: Array<{ date: string; score: number }>;
}

function getAuthHeaders(): HeadersInit {
  const stored = localStorage.getItem('fit2run-admin-auth');
  if (stored) {
    const { state } = JSON.parse(stored);
    if (state?.token) {
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${state.token}`,
      };
    }
  }
  return { 'Content-Type': 'application/json' };
}

async function handleResponse<T>(response: Response): Promise<T> {
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error?.message || 'Request failed');
  }
  return data.data;
}

// Users API
export const usersApi = {
  async list(): Promise<User[]> {
    const response = await fetch(`${API_URL}/api/users`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<User[]>(response);
  },

  async get(id: string): Promise<User> {
    const response = await fetch(`${API_URL}/api/users/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<User>(response);
  },

  async create(user: Partial<User> & { pin: string }): Promise<User> {
    const response = await fetch(`${API_URL}/api/users`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(user),
    });
    return handleResponse<User>(response);
  },

  async update(id: string, user: Partial<User>): Promise<User> {
    const response = await fetch(`${API_URL}/api/users/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(user),
    });
    return handleResponse<User>(response);
  },

  async delete(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/users/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleResponse<void>(response);
  },
};

// Stores API
export const storesApi = {
  async list(): Promise<Store[]> {
    const response = await fetch(`${API_URL}/api/stores`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<Store[]>(response);
  },

  async get(id: string): Promise<Store> {
    const response = await fetch(`${API_URL}/api/stores/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<Store>(response);
  },

  async create(store: Partial<Store>): Promise<Store> {
    const response = await fetch(`${API_URL}/api/stores`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(store),
    });
    return handleResponse<Store>(response);
  },

  async update(id: string, store: Partial<Store>): Promise<Store> {
    const response = await fetch(`${API_URL}/api/stores/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(store),
    });
    return handleResponse<Store>(response);
  },

  async delete(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/stores/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleResponse<void>(response);
  },
};

// Questions API
export const questionsApi = {
  async listGroups(): Promise<QuestionGroup[]> {
    const response = await fetch(`${API_URL}/api/questions/groups`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<QuestionGroup[]>(response);
  },

  async listQuestions(): Promise<Question[]> {
    const response = await fetch(`${API_URL}/api/questions`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<Question[]>(response);
  },

  async createGroup(group: Partial<QuestionGroup>): Promise<QuestionGroup> {
    const response = await fetch(`${API_URL}/api/questions/groups`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(group),
    });
    return handleResponse<QuestionGroup>(response);
  },

  async updateGroup(id: string, group: Partial<QuestionGroup>): Promise<QuestionGroup> {
    const response = await fetch(`${API_URL}/api/questions/groups/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(group),
    });
    return handleResponse<QuestionGroup>(response);
  },

  async createQuestion(question: Partial<Question>): Promise<Question> {
    // Map frontend field names to backend expected names
    const payload = {
      group_id: question.group_id,
      question_text: question.text, // Backend expects question_text, not text
      display_order: question.display_order,
    };
    console.log('createQuestion payload:', payload);
    const response = await fetch(`${API_URL}/api/questions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handleResponse<Question>(response);
  },

  async updateQuestion(id: string, question: Partial<Question>): Promise<Question> {
    // Map frontend field names to backend expected names
    const payload = {
      group_id: question.group_id,
      question_text: question.text, // Backend expects question_text, not text
      display_order: question.display_order,
    };
    const response = await fetch(`${API_URL}/api/questions/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handleResponse<Question>(response);
  },

  async deleteQuestion(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/questions/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleResponse<void>(response);
  },
};

// Cases API
export const casesApi = {
  async list(): Promise<Case[]> {
    const response = await fetch(`${API_URL}/api/cases`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<Case[]>(response);
  },

  async get(id: string): Promise<Case> {
    const response = await fetch(`${API_URL}/api/cases/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<Case>(response);
  },

  async resolve(id: string, notes: string): Promise<Case> {
    const response = await fetch(`${API_URL}/api/cases/${id}/resolve`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ resolution_notes: notes }),
    });
    return handleResponse<Case>(response);
  },

  async updateStatus(id: string, status: string): Promise<Case> {
    const response = await fetch(`${API_URL}/api/cases/${id}/status`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status }),
    });
    return handleResponse<Case>(response);
  },
};

// Reports API
export const reportsApi = {
  async getCompanyReport(): Promise<CompanyReport> {
    const response = await fetch(`${API_URL}/api/reports/company`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<CompanyReport>(response);
  },

  async getStoreReport(storeId: string) {
    const response = await fetch(`${API_URL}/api/reports/store/${storeId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },
};

// Visits API
export const visitsApi = {
  async list(): Promise<Visit[]> {
    const response = await fetch(`${API_URL}/api/visits`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<Visit[]>(response);
  },
};
